/* eslint-disable @typescript-eslint/no-explicit-any */
// components/CKEditorWrapperActionPlan.tsx
import React, { useCallback, useEffect, useRef } from 'react';
import { CKEditor } from 'ckeditor4-react';
import {
    CKEDITOR_CONFIGS,
    FILE_MANAGER_CONFIG,
    CKEditorConfig,
    EditorType
} from '@/utils/ckeditorConfig';
import { installCKEditorFinderBridge } from '@/utils/ckeditorFinderBridge';

interface CKEditorWrapperActionPlanProps {
    type?: EditorType;
    data?: string;
    onChange?: (data: string) => void;
    config?: Partial<CKEditorConfig>;
    className?: string;
    borderRadius?: number | string;
    borderInput?: string;
    placeholder?: string;
    disabled?: boolean;
    resizable?: boolean;
    minHeight?: number | string;
    maxHeight?: number | string;
    onReady?: (editor: any) => void;
    onFocus?: (evt: any) => void;
    onBlur?: (evt: any) => void;
    onError?: (evt: any) => void;
    onOpenFileManager?: (ctx: { type: 'file' | 'image'; insert: (url: string) => void }) => void;
    /**
     * Upload hình ảnh dán vào editor (paste ảnh) → trả về URL file.
     * Nếu không truyền, sẽ bỏ qua xử lý dán ảnh (giữ hành vi CKEditor mặc định).
     */
    onUploadImage?: (file: File) => Promise<string>;
    /** Chặn dán ảnh (file clipboard / base64 / thẻ img). Nút Image trên toolbar vẫn dùng được. */
    disablePasteImage?: boolean;
}

function applyInlineEditorChrome(
    editor: any,
    options: {
        resizable: boolean;
        minHeight: string;
        maxHeight?: string;
        borderRadius: string;
    }
) {
    const editable = editor.editable()?.$ as HTMLElement | undefined;
    if (!editable) return;

    const stripChrome = () => {
        editable.style.outline = 'none';
        if (!editor.focusManager?.hasFocus) {
            editable.style.boxShadow = 'none';
        }

        if (options.resizable) {
            editable.style.resize = 'both';
            editable.style.overflow = 'auto';
            editable.style.minHeight = options.minHeight;
            editable.style.maxHeight = options.maxHeight || 'none';
            editable.style.maxWidth = '100%';
            editable.style.display = 'block';
        }
    };

    stripChrome();
    editable.style.padding = '10px';
    editable.style.lineHeight = '1.7';
    editable.style.background = '#fff';
    editable.style.borderRadius = options.borderRadius;
    editable.style.boxSizing = 'border-box';
    editable.style.width = '100%';

    editor.on('focus', stripChrome);
    editor.on('contentDom', stripChrome);
    editor.on('mode', stripChrome);
}

function isEditorHtmlEmpty(html: string): boolean {
    return !String(html || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
}

function syncEditorPlaceholderDom(editor: any, placeholder?: string) {
    if (!placeholder) return;
    const editable = editor.editable()?.$ as HTMLElement | undefined;
    if (!editable) return;

    const shouldShow =
        isEditorHtmlEmpty(editor.getData()) && !editor.focusManager?.hasFocus;

    if (shouldShow) {
        editable.setAttribute('data-cke-editorplaceholder', placeholder);
    } else {
        editable.removeAttribute('data-cke-editorplaceholder');
    }
}

function attachPlaceholderBehavior(editor: any, placeholder?: string) {
    if (!placeholder) return;
    const flag = '__ckPlaceholderAttached';
    const sync = () => syncEditorPlaceholderDom(editor, placeholder);

    if (editor[flag]) {
        sync();
        return;
    }
    editor[flag] = true;

    sync();
    editor.on('change', sync);
    editor.on('focus', sync);
    editor.on('blur', sync);
    editor.on('setData', sync);
    editor.on('contentDom', sync);
    editor.on('dataReady', sync);
}

function attachLinkClickHandler(editor: any) {
    const dom = editor.editable()?.$ as HTMLElement | undefined;
    if (!dom) return;

    const flag = '__ckLinkClickAttached';
    const marked = dom as HTMLElement & Record<string, boolean>;
    if (marked[flag]) return;
    marked[flag] = true;

    const onClick = (e: MouseEvent) => {
        const anchor = (e.target as HTMLElement | null)?.closest?.('a');
        const href = anchor?.getAttribute('href');
        if (!href) return;

        e.preventDefault();
        e.stopPropagation();
        window.open(href, '_blank', 'noopener,noreferrer');
    };

    dom.addEventListener('click', onClick, true);
    editor.on('destroy', () => {
        dom.removeEventListener('click', onClick, true);
        delete marked[flag];
    });
}

/** Chuyển dataURL (base64) thành File để upload lên server. */
function dataUrlToImageFile(dataUrl: string): File {
    const meta = (dataUrl.match(/^data:(.*?)(;base64)?,/) || [])[1] || 'image/png';
    const base64 = dataUrl.includes(';base64,') ? dataUrl.split(',')[1] : dataUrl.split(',')[0];
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const mime = meta || 'image/png';
    const ext = (mime.split('/')[1] || 'png').replace('jpeg', 'jpg');
    return new File([arr], `pasted-${Date.now()}.${ext}`, { type: mime });
}

function clipboardHasImageFiles(evt: any): { hasImage: boolean; files: File[] } {
    let pastedFiles: File[] = [];
    const dt = evt.data && evt.data.dataTransfer;
    if (dt && typeof dt.getFiles === 'function') {
        pastedFiles = dt.getFiles() || [];
    } else if (evt.data && evt.data.$ && evt.data.$.clipboardData) {
        pastedFiles = Array.from(evt.data.$.clipboardData.files || []);
    }

    const hasFileImage = pastedFiles.some(
        (f) => f && f.type && f.type.startsWith('image/')
    );
    if (hasFileImage) return { hasImage: true, files: pastedFiles };

    // Clipboard items (screenshot): thường không có FileList — lấy file từ items.
    const items = evt.data?.$?.clipboardData?.items;
    if (items) {
        const fromItems: File[] = [];
        Array.from(items as DataTransferItem[]).forEach((i) => {
            if (!i || !i.type || !i.type.startsWith('image/')) return;
            const f = typeof i.getAsFile === 'function' ? i.getAsFile() : null;
            if (f) fromItems.push(f);
        });
        if (fromItems.length > 0) return { hasImage: true, files: fromItems };
        const hasItemImage = Array.from(items as DataTransferItem[]).some(
            (i) => i && i.type && i.type.startsWith('image/')
        );
        if (hasItemImage) return { hasImage: true, files: pastedFiles };
    }

    return { hasImage: false, files: pastedFiles };
}

function stripPastedImages(html: string): string {
    return html
        .replace(/<img\b[^>]*>/gi, '')
        .replace(/data:image\/[a-z0-9+.-]+;base64,[A-Za-z0-9+/=\s]+/gi, '');
}

/**
 * Cho phép dán hình ảnh vào editor: bắt sự kiện paste, tải ảnh lên server
 * qua `onUploadImage` rồi chèn `<img src="url">` tại vị trí con trỏ
 * (thay vì nhúng base64 ảnh khổng lồ vào HTML).
 * `disablePasteImage` chặn dán ảnh, giữ phần text nếu có.
 */
function attachPasteImageHandler(
    editor: any,
    onUploadImage?: (file: File) => Promise<string>,
    disablePasteImage?: boolean
) {
    if (!disablePasteImage && !onUploadImage) return;
    const dom = editor.editable()?.$ as HTMLElement | undefined;
    if (!dom) return;

    const flag = '__ckPasteImageAttached';
    const marked = dom as HTMLElement & Record<string, boolean>;
    if (marked[flag]) return;
    marked[flag] = true;

    const insertAtCaret = (html: string) => {
        const selection = editor.getSelection();
        const range = selection && selection.getRanges(true).length
            ? selection.getRanges(true)[0]
            : null;
        if (range && selection) {
            selection.selectRanges([range]);
        }
        editor.insertHtml(html);
        // Async paste/upload: bắt buộc fire change để Form.Item nhận HTML có <img>.
        try {
            editor.fire('change');
            editor.fire('saveSnapshot');
        } catch {
            /* noop */
        }
    };

    const insertImage = (url: string, alt: string) => {
        insertAtCaret(`<img src="${url}" alt="${alt}" style="max-width:100%;" />`);
    };

    const uploadFiles = async (files: File[]) => {
        if (!onUploadImage) return;
        const imageFiles = files.filter((f) => f && f.type && f.type.startsWith('image/'));
        if (imageFiles.length === 0) return;
        for (const file of imageFiles) {
            try {
                const url = await onUploadImage(file);
                if (url) insertImage(url, file.name);
            } catch (err) {
                console.error('[CKEditor] upload pasted image failed', err);
            }
        }
    };

    const uploadBase64Images = async (html: string) => {
        if (!onUploadImage) return;
        const regex = /src="(data:image\/(?:png|jpe?g|gif|webp|bmp);base64,[^"]+)"/gi;
        const matches: string[] = [];
        html.replace(regex, (m: string, p1: string) => {
            matches.push(p1);
            return m;
        });
        if (matches.length === 0) return;

        let finalHtml = html;
        for (const dataUrl of matches) {
            let uploadedUrl = dataUrl;
            try {
                const url = await onUploadImage(dataUrlToImageFile(dataUrl));
                if (url) uploadedUrl = url;
            } catch (err) {
                console.error('[CKEditor] upload pasted base64 image failed', err);
            }
            finalHtml = finalHtml.replace(dataUrl, uploadedUrl);
        }
        insertAtCaret(finalHtml);
    };

    editor.on('paste', (evt: any) => {
        const { hasImage: hasImageFiles, files: pastedFiles } = clipboardHasImageFiles(evt);
        const dataValue = (evt.data && evt.data.dataValue) || '';
        const htmlHasImage =
            /<img\b/i.test(dataValue) ||
            /data:image\/(?:png|jpe?g|gif|webp|bmp);base64,/i.test(dataValue);

        if (disablePasteImage) {
            if (hasImageFiles) {
                evt.cancel();
                evt.stop();
                return;
            }
            if (htmlHasImage) {
                const stripped = stripPastedImages(dataValue);
                const leftoverText = stripped
                    .replace(/<[^>]*>/g, '')
                    .replace(/&nbsp;/gi, ' ')
                    .trim();
                if (!leftoverText) {
                    evt.cancel();
                    evt.stop();
                    return;
                }
                evt.data.dataValue = stripped;
            }
            return;
        }

        if (hasImageFiles) {
            evt.cancel();
            evt.stop();
            void uploadFiles(pastedFiles);
            return;
        }

        // Ảnh base64 dán trực tiếp trong HTML (vd. copy ảnh từ trang web)
        if (/data:image\/(?:png|jpe?g|gif|webp|bmp);base64,/i.test(dataValue)) {
            evt.cancel();
            evt.stop();
            void uploadBase64Images(dataValue);
        }
    });

    editor.on('destroy', () => {
        delete marked[flag];
    });
}

const CKEditorWrapperActionPlanStyles: React.FC<{ disabled: boolean }> = ({ disabled }) => (
    <style jsx>{`
        .ckeditor-wrapper {
            position: relative;
        }

        .ckeditor-wrapper :global(.cke_editable),
        .ckeditor-wrapper :global(.cke_editable_inline) {
            opacity: ${disabled ? 0.6 : 1};
            pointer-events: auto;
        }

        .ckeditor-wrapper :global(.cke_editable a),
        .ckeditor-wrapper :global(.cke_editable_inline a) {
            color: #0c68e9 !important;
            text-decoration: underline !important;
            cursor: pointer !important;
            pointer-events: auto !important;
        }

        .ckeditor-wrapper :global(.cke_editable a:hover),
        .ckeditor-wrapper :global(.cke_editable_inline a:hover) {
            color: #0958d9 !important;
        }

        .ckeditor-wrapper :global(.cke_toolbar) {
            opacity: ${disabled ? 0.6 : 1};
            pointer-events: ${disabled ? 'none' : 'auto'};
        }

        .ckeditor-wrapper--resizable {
            background: #fff;
        }

        .ckeditor-wrapper--resizable :global(.cke),
        .ckeditor-wrapper--resizable :global(.cke_inner),
        .ckeditor-wrapper--resizable :global(.cke_top),
        .ckeditor-wrapper--resizable :global(.cke_bottom) {
            border: none !important;
            box-shadow: none !important;
        }

        .ckeditor-wrapper--resizable :global(.cke_editable),
        .ckeditor-wrapper--resizable :global(.cke_editable_inline) {
            border: 1px solid #e5e7eb !important;
            box-shadow: none !important;
            outline: none !important;
            transition: border-color 0.2s, box-shadow 0.2s;
        }

        .ckeditor-wrapper--resizable :global(.cke_editable:hover),
        .ckeditor-wrapper--resizable :global(.cke_editable_inline:hover) {
            border-color: #4096ff !important;
        }

        .ckeditor-wrapper--resizable :global(.cke_editable.cke_focus),
        .ckeditor-wrapper--resizable :global(.cke_editable_inline.cke_focus),
        .ckeditor-wrapper--resizable :global(.cke_editable_inline:focus) {
            border-color: #4096ff !important;
            box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.1) !important;
        }

        .ckeditor-wrapper--resizable :global(.cke_editable_inline) {
            resize: both;
            overflow: auto;
            display: block;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
        }

        .ckeditor-wrapper--has-placeholder :global(.cke_editable[data-cke-editorplaceholder]),
        .ckeditor-wrapper--has-placeholder :global(.cke_editable_inline[data-cke-editorplaceholder]) {
            position: relative;
        }

        .ckeditor-wrapper--has-placeholder :global(.cke_editable[data-cke-editorplaceholder]::before),
        .ckeditor-wrapper--has-placeholder :global(.cke_editable_inline[data-cke-editorplaceholder]::before) {
            content: attr(data-cke-editorplaceholder);
            color: rgba(0, 0, 0, 0.25);
            pointer-events: none;
            position: absolute;
            top: 10px;
            left: 10px;
            right: 10px;
            display: block;
            line-height: 1.7;
        }
    `}</style>
);

const CKEditorWrapperActionPlan: React.FC<CKEditorWrapperActionPlanProps> = ({
    type = 'classic',
    data = '',
    onChange,
    config = {},
    className = '',
    borderRadius = 4,
    borderInput = '1px solid #ccc',
    placeholder,
    disabled = false,
    resizable = false,
    minHeight = 100,
    maxHeight,
    onReady,
    onFocus,
    onBlur,
    onError,
    onOpenFileManager,
    onUploadImage,
    disablePasteImage = false,
}) => {
    const editorRef = useRef<any>(null);
    const latestDataRef = useRef<string>(data || '');
    const skipNextDataPropSyncRef = useRef(false);
    const resolvedBorderRadius = typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;
    const resolvedMinHeight = typeof minHeight === 'number' ? `${minHeight}px` : minHeight;
    const resolvedMaxHeight = maxHeight
        ? (typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight)
        : undefined;

    useEffect(() => {
        installCKEditorFinderBridge();
    }, []);

    useEffect(() => {
        latestDataRef.current = data || '';
    }, [data]);

    const baseRemovePlugins = CKEDITOR_CONFIGS[type].removePlugins || '';
    const customRemovePlugins = config.removePlugins || '';
    const allRemovePlugins = [
        baseRemovePlugins,
        customRemovePlugins,
        'exportpdf',
        placeholder ? 'editorplaceholder' : '',
    ]
        .filter(Boolean)
        .join(',')
        .split(',')
        .filter((plugin, index, arr) => arr.indexOf(plugin) === index)
        .join(',');

    const editorConfig: CKEditorConfig = {
        ...CKEDITOR_CONFIGS[type],
        ...FILE_MANAGER_CONFIG,
        ...config,
        readOnly: disabled,
        removePlugins: allRemovePlugins,
        ...(placeholder
            ? {
                startupFocus: config.startupFocus ?? false,
            }
            : {}),
    };

    if (onOpenFileManager) {
        (editorConfig as any).filebrowserBrowseUrl = undefined;
        (editorConfig as any).filebrowserImageBrowseUrl = undefined;
        (editorConfig as any).filebrowserBrowseFunc = () => {
            onOpenFileManager({
                type: 'file',
                insert: (url: string) => {
                    if (editorRef.current) {
                        editorRef.current.insertHtml(`<a href="${url}" target="_blank" rel="noopener">${url}</a>`);
                    }
                }
            });
        };
        (editorConfig as any).filebrowserImageBrowseFunc = () => {
            onOpenFileManager({
                type: 'image',
                insert: (url: string) => {
                    if (editorRef.current) {
                        editorRef.current.insertHtml(`<img src="${url}" alt="" />`);
                    }
                }
            });
        };
    }

    const handleChange = useCallback((evt: any) => {
        const newData = evt.editor.getData();
        latestDataRef.current = newData;
        skipNextDataPropSyncRef.current = true;
        onChange?.(newData);
    }, [onChange]);

    const handleReady = useCallback((evt: any) => {
        const editor = evt.editor;
        editorRef.current = editor;

        setTimeout(() => {
            try {
                const currentData = editor.getData();
                const incoming = latestDataRef.current;
                const applyPlaceholder = () => attachPlaceholderBehavior(editor, placeholder);

                if (incoming !== currentData) {
                    editor.setData(incoming, { callback: applyPlaceholder });
                } else {
                    applyPlaceholder();
                }

                attachLinkClickHandler(editor);
                editor.on('contentDom', () => attachLinkClickHandler(editor));

                attachPasteImageHandler(editor, onUploadImage, disablePasteImage);

                if (type === 'inline') {
                    applyInlineEditorChrome(editor, {
                        resizable,
                        minHeight: resolvedMinHeight,
                        maxHeight: resolvedMaxHeight,
                        borderRadius: resolvedBorderRadius,
                    });
                }

                if (placeholder) {
                    window.setTimeout(() => syncEditorPlaceholderDom(editor, placeholder), 0);
                    window.setTimeout(() => syncEditorPlaceholderDom(editor, placeholder), 250);
                }
            } catch {
                /* noop */
            }
            onReady?.(editor);
        }, 100);
    }, [onReady, type, resizable, resolvedMinHeight, resolvedMaxHeight, resolvedBorderRadius, placeholder, onUploadImage, disablePasteImage]);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;
        try {
            editor.setReadOnly(!!disabled);
        } catch {
            /* noop */
        }
    }, [disabled]);

    const handleFocus = useCallback((evt: any) => {
        onFocus?.(evt);
    }, [onFocus]);

    const handleBlur = useCallback((evt: any) => {
        onBlur?.(evt);
    }, [onBlur]);

    const handleError = useCallback((evt: any) => {
        if (evt?.data?.errorCode === 'exportpdf-no-token-url') {
            return;
        }
        console.error('CKEditor error:', evt);
        onError?.(evt);
    }, [onError]);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;
        if (skipNextDataPropSyncRef.current) {
            skipNextDataPropSyncRef.current = false;
            return;
        }
        try {
            const current = editor.getData();
            if (data === undefined || data === current) {
                return;
            }
            if (editor.focusManager?.hasFocus === true) {
                return;
            }
            setTimeout(() => {
                const ed = editorRef.current;
                if (!ed || ed.focusManager?.hasFocus) {
                    return;
                }
                const cur = ed.getData();
                if ((data ?? '') === cur) {
                    return;
                }
                ed.setData(data || '', {
                    callback: () => syncEditorPlaceholderDom(ed, placeholder),
                });
            }, 50);
        } catch {
            /* noop */
        }
    }, [data, placeholder]);

    const wrapperClassName = `ckeditor-wrapper ${resizable ? 'ckeditor-wrapper--resizable' : ''} ${placeholder ? 'ckeditor-wrapper--has-placeholder' : ''} ${className}`.trim();

    if (type === 'inline') {
        return (
            <div className={wrapperClassName}>
                <CKEditor
                    key={`inline-${type}`}
                    type="inline"
                    initData={data}
                    config={editorConfig}
                    onChange={handleChange}
                    onInstanceReady={handleReady}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onError={handleError}
                    style={{
                        minHeight: resolvedMinHeight,
                        padding: '10px',
                        border: 'none',
                        borderRadius: resolvedBorderRadius,
                        opacity: disabled ? 0.6 : 1,
                        lineHeight: '1.7',
                        outline: 'none',
                        boxShadow: 'none',
                    }}
                />
                <CKEditorWrapperActionPlanStyles disabled={disabled} />
            </div>
        );
    }

    return (
        <div className={wrapperClassName}>
            <CKEditor
                key={`classic-${type}`}
                initData={data}
                config={editorConfig}
                onChange={handleChange}
                onInstanceReady={handleReady}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onError={handleError}
                style={{
                    border: borderInput,
                    borderRadius: resolvedBorderRadius,
                }}
            />
            <CKEditorWrapperActionPlanStyles disabled={disabled} />
        </div>
    );
};

export default CKEditorWrapperActionPlan;
